/* eslint-disable @typescript-eslint/no-explicit-any */
// #region Imports
import { useEffect, useRef, useState } from "react";
import { IntlProvider } from "react-intl";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import {
  setPortalDefaultLanguage,
  setSiteLanguage,
  setTheme,
} from "../../appSlice";
import LanguageContext from "../../libs/context/LanguageContext";
import { useClientAppSettings, useLanguage, useToken } from "../../libs/hooks";
import { ClientDetails } from "../../libs/models/accountModel";
import { AccountApi, LocalisationApi } from "../../libs/services";
import { useAppDispatch } from "../../store";
import IntlProviderForKendoControls from "../intl/intlProviderForKendoControls";
import { ApplicationRoutes } from "../routes/config";
import AppRoutes from "../routes/routes";
import Loading from "../shared/wrappers/loading";

// import { isDevelopment } from "../../libs/helpers";
// #endregion

function resolveAppNameFromLocation(pathname: string, search: string): string {
  // client name can either be in the path (http://connection.local:3001/icportal/basenhsdev) or
  // can be in the search query string (http://connection.local:3001/icportal/login?redirect=/BaseNhsDev)
  // if the client name is in the search query string then use that
  if (search) {
    const urlParams = new URLSearchParams(search);
    return urlParams?.get("redirect")?.replace("/", "") ?? "";
  }
  // if the client name is not in the search query string, then check the path for client name
  // exclude application routes from the check
  if (
    !Object.values(ApplicationRoutes).some((path) => {
      return matchPath({ path, end: true }, pathname) != null;
    })
  ) {
    return pathname.replace("/", "");
  }
  return "";
}

// #region Component
// eslint-disable-next-line @typescript-eslint/naming-convention
function RootComponent(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const initialAppNameFromUrl = resolveAppNameFromLocation(
    location.pathname,
    location.search
  );
  const [isClientBootstrapComplete, setIsClientBootstrapComplete] = useState(
    () => initialAppNameFromUrl === ""
  );

  // state
  const defaultMessages: Record<string, string> = {};
  const [messages, setMessages] = useState(defaultMessages);
  const { app, setClientApp } = useClientAppSettings();
  const activeAppName = useRef<string>("");
  const [activeSiteLanguage, setActiveSiteLanguage] = useState("");
  const isLoginPage = useRef<boolean>(true);
  const isSiteLanguageAvailableInRedux = useRef<boolean>(false); // Is site lang available in redux
  const { getClientAppSettings } = useClientAppSettings();
  const { getSiteLanguageByAppName, getSiteLanguage } = useLanguage();
  const { setToken } = useToken();
  const queryString = location.search;
  const urlParams = new URLSearchParams(queryString);
  const tokenFromUrl = urlParams?.get("token") ?? "";
  useEffect(() => {
    if (tokenFromUrl) setToken({ token: tokenFromUrl });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Resolve client name from the URL
   * Update the site language if there is already value in redux for site language
   * if it is new client then call setClientApp to create the session
   */
  useEffect(() => {
    const appNameFromUrl = resolveAppNameFromLocation(
      location.pathname,
      location.search
    );
    //Set the Active app name from URL
    activeAppName.current = appNameFromUrl;

    // Get the current location to determine the if the active page in login
    const activeLocationPathName = window.location.pathname
      .replace("/icportal", "")
      .replace("/portal", "");
    isLoginPage.current =
      activeLocationPathName === ApplicationRoutes.Login.toString() ||
      activeLocationPathName === "/";
    //get the the last saved lang from redux
    //If login page then use the app name from URL else get the value from session
    const siteLangFromRedux = isLoginPage.current
      ? getSiteLanguageByAppName(appNameFromUrl)
      : getSiteLanguage();

    //Set the active lang
    setActiveSiteLanguage(siteLangFromRedux);

    if (appNameFromUrl) {
      // Flag if the lang was found from redux
      isSiteLanguageAvailableInRedux.current = siteLangFromRedux !== "";
      // if the name resolved from the URL is not what we currently have in session
      // then update session
      if (appNameFromUrl.toLowerCase() !== (app.AppName?.toLowerCase() ?? "")) {
        //Init Client to session
        setClientApp({
          AppName: appNameFromUrl,
          ClientName: "",
          ClientFullName: "",
          ClientLogo: "",
          CopyrightYear: "",
          ForceReadOnly: false,
          DisableSelfRegistration: false,
          DatabaseLock: true,
          DatabaseLockText: "",
          siteLanguage: "",
          portalDefaultLanguage: "",
          ShowForgotPasswordLink: false,
          dateFormat: "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clientDetailRef = useRef<ClientDetails>({} as ClientDetails);
  useEffect(() => {
    if (activeAppName.current && activeAppName.current !== "") {
      AccountApi.getClient("en")
        .then((res) => {
          if (res.responseCode === 200 && res.data) {
            clientDetailRef.current = res.data as ClientDetails;
            const clientDetail = clientDetailRef.current;
            // by default get language to "en"
            let activeLanguage = "en";

            // use language from browser's setting if available
            if (navigator.language)
              activeLanguage = navigator.language.toLowerCase();

            // use portal default language if available
            if (clientDetail.portalDefaultLanguage)
              activeLanguage = clientDetail.portalDefaultLanguage;

            //update the session store
            setClientApp({
              ...getClientAppSettings(),
              ClientName: clientDetail.name,
              ClientFullName: clientDetail.appName ?? "",
              CopyrightYear: clientDetail.copyrightYear,
              ClientLogo:
                process.env.PUBLIC_URL +
                (clientDetail.clientLogo
                  ? "/client-logos/" + clientDetail.clientLogo
                  : process.env.PUBLIC_URL + "/AppLogo.png"),
              ForceReadOnly: clientDetail.forceReadOnlyDBLock,
              DisableSelfRegistration: clientDetail.disableSelfRegistration,
              DatabaseLock: clientDetail.databaseLock,
              DatabaseLockText: clientDetail.databaseLockText,
              portalDefaultLanguage: clientDetail.portalDefaultLanguage, //Since this is session specific no need to append client name
              ShowForgotPasswordLink: clientDetail.showForgotPasswordLink,
              dateFormat: clientDetail.dateFormat,
            });

            // we have a user preferred language then update the language
            // or update the lang based on calculation done above to determine the value from activeLanguage
            if (clientDetail.userPreferredLanguage)
              updateLocale(
                clientDetail.userPreferredLanguage,
                activeAppName.current
              );
            else if (!isSiteLanguageAvailableInRedux.current)
              updateLocale(activeLanguage, activeAppName.current);

            dispatch(
              setPortalDefaultLanguage({
                key: clientDetail.name,
                value: clientDetail.portalDefaultLanguage,
              })
            );

            dispatch(
              setTheme({ key: clientDetail.name, value: clientDetail.theme })
            );
          }
        })
        .finally(() => {
          setIsClientBootstrapComplete(true);
        });
    } else {
      setIsClientBootstrapComplete(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Get translations from api call whenever the activeLanguage changes and update the state
   */
  useEffect(() => {
    if (activeSiteLanguage !== "") {
      LocalisationApi.getTranslations(activeSiteLanguage).then((res) => {
        if (res.responseCode === 200 && res.data !== undefined) {
          setMessages(res.data);
        }
      });

      // as language is changed, get the client details again as it contains language specific data -
      // database lock text and client full name
      AccountApi.getClient(activeSiteLanguage).then((res) => {
        if (res.responseCode === 200 && res.data) {
          setClientApp({
            ...getClientAppSettings(),
            ClientFullName: res.data.appName ?? "",
            DatabaseLockText: res.data.databaseLockText,
            dateFormat: res.data.dateFormat,
          });
        }
      });
    }

    // Developer only, un-comment when needed to refresh the resource entries
    // insertDefaultResources();
  }, [activeSiteLanguage]);
  /**
   * This method is called for update the language in session and redux and also to update the language resource entries
   * @param selectedLocale  // the selected locale is language value which user has selected
   * @param currentClient  // current client name
   */
  function updateLocale(selectedLocale: string, currentClient: string) {
    if (
      currentClient &&
      currentClient !== "" &&
      selectedLocale &&
      selectedLocale !== ""
    ) {
      //update the session store
      setClientApp({
        ...getClientAppSettings(),
        siteLanguage: selectedLocale, //Since this is session specific no need to append client name
      });

      // Update the redux store
      dispatch(
        setSiteLanguage({
          key: currentClient,
          value: selectedLocale,
        })
      );

      //Update the Active Language to trigger update the language resource entries
      setActiveSiteLanguage(selectedLocale);
    }
  }

  // if we do not have the client's full name e.g. IQVIA Technology Services, then redirect to 404
  // this is only possible if the app name is not valid as all valid app names have a full name
  if (clientDetailRef.current.name && !clientDetailRef.current.appName) {
    // do not redirect if we are already on the 404 page
    if (location.pathname !== ApplicationRoutes.Portal404) navigate("/page404");
  }

  // if the client bootstrap is not complete, then show the loading spinner
  // client bootstrap is complete when the client details are fetched and the client is set in the session
  if (!isClientBootstrapComplete) {
    return <Loading />;
  }

  return (
    <LanguageContext.Provider
      value={{ locale: activeSiteLanguage, updateLocale: updateLocale }}
    >
      <IntlProviderForKendoControls messages={messages}>
        {/* Empty string breaks react-intl v7 until language hydrates; fallback keeps formatjs valid. LanguageContext still exposes raw `activeSiteLanguage`. */}
        <IntlProvider
          locale={activeSiteLanguage || "en"}
          defaultLocale="en"
          messages={messages}
        >
          <AppRoutes />
        </IntlProvider>
      </IntlProviderForKendoControls>
    </LanguageContext.Provider>
  );
}
export default RootComponent;
// #endregion

// #region Functions
// Developer only
// const insertDefaultResources = () => {
//   const getMessagesFromFileAndUpdateBackend = (resourceSet: string) => {
//     const messageLoader = () => import("../../messages/" + resourceSet);
//     messageLoader().then((messages: any) => {
//       const resources = messages.default;
//       let k: keyof typeof resources;
//       for (k in resources) {
//         const v = resources[k];
//         LocalisationApi.addTranslation({
//           id: "Portal." + v.id,
//           defaultMessage: v.defaultMessage,
//         });
//       }
//     });
//   };

//   if (isDevelopment()) {
// getMessagesFromFileAndUpdateBackend("applicationMessages");
// getMessagesFromFileAndUpdateBackend("clinicianAssessmentMessages");
// getMessagesFromFileAndUpdateBackend("commentReviewReportMessages");
// getMessagesFromFileAndUpdateBackend("contactUsMessages");
// getMessagesFromFileAndUpdateBackend("dashboardMessages");
// getMessagesFromFileAndUpdateBackend("fileUploadMessages");
// getMessagesFromFileAndUpdateBackend("forgotPasswordMessages");
// getMessagesFromFileAndUpdateBackend("gasMessages");
// getMessagesFromFileAndUpdateBackend("globalPortalMessages");
// getMessagesFromFileAndUpdateBackend("homeMessages");
// getMessagesFromFileAndUpdateBackend("loginPageMessages");
// getMessagesFromFileAndUpdateBackend("myAccountMessages");
// getMessagesFromFileAndUpdateBackend("myDataMessages");
// getMessagesFromFileAndUpdateBackend("myImpactMessages");
// getMessagesFromFileAndUpdateBackend("newsletterMessage");
// getMessagesFromFileAndUpdateBackend("optOutMessages");
// getMessagesFromFileAndUpdateBackend("participants");
// getMessagesFromFileAndUpdateBackend("participantSearchMessages");
// getMessagesFromFileAndUpdateBackend("registrationMessages");
// getMessagesFromFileAndUpdateBackend("resourcesMessage");
// getMessagesFromFileAndUpdateBackend("setPasswordMessages");
// getMessagesFromFileAndUpdateBackend("surveyMessages");
// getMessagesFromFileAndUpdateBackend("userMessages");
// getMessagesFromFileAndUpdateBackend("validateMessages");
// getMessagesFromFileAndUpdateBackend("viewSubmissions");
// getMessagesFromFileAndUpdateBackend("kendo/fileUpload");
// getMessagesFromFileAndUpdateBackend("kendo/datePicker");
// getMessagesFromFileAndUpdateBackend("kendo/dropdowns");
// getMessagesFromFileAndUpdateBackend("kendo/grid");
//}
// };
// #endregion
